import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminPageHeader, AdminEmpty } from "@/components/admin/AdminBits";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["Active", "Pending", "Suspended", "Inactive"];
const empty = { name: "", country: "", status: "Active" };

export default function AdminFederations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/federations"); setItems(data); } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (f) => { setForm({ name: f.name, country: f.country, status: f.status }); setEditId(f.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.country.trim()) return toast.error("Name and country are required");
    setSaving(true);
    try {
      if (editId) await api.put(`/admin/federations/${editId}`, form);
      else await api.post("/admin/federations", form);
      toast.success(editId ? "Federation updated" : "Federation added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const del = async () => {
    try { await api.delete(`/admin/federations/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const field = "w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20";

  return (
    <div data-testid="admin-federations-page">
      <AdminPageHeader title="Federations" subtitle="Manage the affiliated federation directory."
        action={<button onClick={openNew} data-testid="federation-add-button" className="inline-flex items-center gap-2 rounded-full bg-ukf-red px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-ukf-red-dark active:scale-[0.98]"><Plus className="h-4 w-4" /> Add federation</button>} />

      {loading ? <p className="text-sm text-slate-400">Loading…</p> : items.length === 0 ? (
        <AdminEmpty title="No federations yet" message="Add your first affiliated federation." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Country</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((f) => (
                <tr key={f.id} data-testid={`federation-row-${f.id}`} className="hover:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-ukf-ink">{f.name}</td>
                  <td className="px-5 py-3 text-slate-500">{f.country}</td>
                  <td className="px-5 py-3"><StatusBadge status={f.status} /></td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(f)} data-testid={`federation-edit-${f.id}`} className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-ukf-red"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setDelId(f.id)} data-testid={`federation-delete-${f.id}`} className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-red-50 hover:text-ukf-red"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="federation-editor-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit federation" : "Add federation"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Federation name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="federation-name-input" className={field} /></div>
            <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Country</label><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="federation-country-input" className={field} /></div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="federation-status-select" className={field}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setOpen(false)} className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="federation-save-button" className="rounded-full bg-ukf-red px-5 py-2.5 text-sm font-semibold text-white hover:bg-ukf-red-dark disabled:opacity-60">{editId ? "Update" : "Save"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove this federation?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} data-testid="federation-confirm-delete" className="bg-ukf-red hover:bg-ukf-red-dark">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
